import { Column } from '@/src/components/common/Column';
import { Markdown } from '@/src/components/common/Markdown';
import MarkDownPreviewEditor from '@/src/components/common/MarkdownPreviewEditor/MarkDownPreviewEditor';
import { TranslatedAttribute } from '@/src/types/common';
import { getLocaleLabel } from '@/src/utils/locale.utils';
import {
  ColumnLayout,
  ExpandableSection,
  FormField,
  Input,
  SpaceBetween,
  TextContent,
} from '@amzn/awsui-components-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { i18nKeys } from '../../../../utils/i18n.utils';

interface ChallengeTranslationDetailProps {
  formUpdate: ({ attributeName, originalText, translatedText }: TranslatedAttribute) => void;
  attributeName?: string;
  heading?: string;
  title?: { label: string; value: string };
  originalTitle?: { label: string; value: string };
  description?: { label: string; value: string };
  originalDescription?: { label: string; value: string };
  editor?: boolean;
}

const ChallengeTranslationDetail: React.FC<ChallengeTranslationDetailProps> = ({
  formUpdate,
  attributeName,
  title,
  originalTitle,
  description,
  originalDescription,
  heading,
  editor = false,
}) => {
  const { t } = useTranslation();

  const [newDescription, setNewDescription] = useState(description?.value || '');
  const [newTitle, setTitle] = useState(title?.value || '');

  const onFormChange = ({
    key,
    attrName,
    translatedText,
    originalText,
  }: {
    key: string;
    attrName: string;
    translatedText: string;
    originalText: string;
  }) => {
    if (key === 'title') {
      setTitle(translatedText);
    }
    if (key === 'description') {
      setNewDescription(translatedText);
    }
    void formUpdate({ attributeName: attrName, originalText, translatedText });
  };

  const attributes = ['title', 'description', 'category'];

  const getAttributeName = (attr: string | undefined, forTitle = false) => {
    if (!attr) {
      return '';
    }
    if (attributes.includes(attr)) {
      return attr;
    }
    if (forTitle) {
      return `${attr}.title`;
    }
    if (attr.indexOf('task.') > -1) {
      return `${attr}.content`;
    }
    return `${attr}.description`;
  };

  useEffect(() => {
    setNewDescription(description?.value || '');
  }, [description?.value]);

  useEffect(() => {
    setTitle(title?.value || '');
  }, [title?.value]);

  return (
    <ExpandableSection defaultExpanded variant="container" headerText={heading || title?.label || 'Heading'}>
      <SpaceBetween size="s">
        <ExpandableSection
          variant="container"
          headerText={t(i18nKeys.challenges.challengeDetails.titles.originalValueLabel, {
            language: getLocaleLabel('en'),
          })}
        >
          <ColumnLayout columns={1}>
            {originalTitle && (
              <Column size="s">
                <FormField label={originalTitle?.label || 'Title'}>
                  <Input disabled value={originalTitle.value} />
                </FormField>
              </Column>
            )}
            {originalDescription && (
              <Column size="s">
                <FormField label={originalDescription.label}>
                  <Markdown content={originalDescription.value || ''} allowHtml />
                </FormField>
              </Column>
            )}
          </ColumnLayout>
        </ExpandableSection>
        {title && (
          <ColumnLayout columns={2}>
            <Column size="s">
              <FormField label={title?.label || 'Title'}>
                <Input
                  data-testid={`challenge-translation__${getAttributeName(attributeName, true)}-input`}
                  onChange={({ detail }) =>
                    onFormChange({
                      key: 'title',
                      attrName: getAttributeName(attributeName, true),
                      translatedText: detail.value,
                      originalText: title?.value,
                    })
                  }
                  value={newTitle || ''}
                />
              </FormField>
            </Column>
            <Column size="s">
              <FormField label="Preview">
                <TextContent>{newTitle || ''}</TextContent>
              </FormField>
            </Column>
          </ColumnLayout>
        )}
        {editor && (
          <MarkDownPreviewEditor
            activeTabIndex={2}
            text={newDescription || ''}
            data-testid={`challenge-translation__${getAttributeName(attributeName)}-markdown`}
            onTextChanged={(e) => {
              if (e !== description?.value) {
                onFormChange({
                  key: 'description',
                  attrName: getAttributeName(attributeName),
                  translatedText: e,
                  originalText: originalDescription?.value || '',
                });
              }
            }}
          />
        )}
      </SpaceBetween>
    </ExpandableSection>
  );
};

export default ChallengeTranslationDetail;
